import { cx } from '@emotion/css';
import { InfoOutlined } from '@mui/icons-material';
import { Divider } from '@sortlist-frontend/design-system/legacy';
import { Trans } from '@sortlist-frontend/translation/ssr';
import { useState } from 'react';

import type { Section } from '_features/AgencyProfile/utils';

import { ServiceAbout } from './ServiceAbout';
import { ServiceClients } from './ServiceClients';
import { ServicePreview } from './ServicePreview';
import { ServiceReviews } from './ServiceReviews';
import { maxNumberOfServicesDisplayed } from './ServicesPanel';
import { ServiceWorks } from './ServiceWorks';
import { extractClientsFromWorks, type ServiceNode, type ServiceWorkNode } from './types';

interface Props {
  agencySlug: string;
  className?: string;
  handleActivePanel: (section?: Section) => void;
  index: number;
  service: ServiceNode;
  servicesCount: number;
  totalWorksCount: number;
  visibleServicesCount: number;
  serviceUrl: string;
}

export const ServiceCard = (props: Props) => {
  const {
    agencySlug,
    className,
    handleActivePanel,
    index,
    service,
    serviceUrl,
    servicesCount,
    totalWorksCount,
    visibleServicesCount,
  } = props;

  const [toggled, setToggled] = useState(false);

  const worksCount = service.works.totalCount;
  const reviewsCount = service.reviews.totalCount;
  const skillsCount = service.skills.totalCount;
  const descriptionLength = service.description?.length ?? 0;
  const clients = extractClientsFromWorks(service.works);
  const works = (service.works.nodes ?? []).filter((w): w is ServiceWorkNode => w != null);
  const reviews = service.reviews.nodes ?? [];
  const skills = service.skills.nodes ?? [];
  const serviceName = service.expertise.name ?? '';

  const hasInfo = worksCount + descriptionLength + skillsCount + clients.length + reviewsCount + worksCount > 0;

  const lastItem: boolean = visibleServicesCount === index + 1 || servicesCount === index + 1;
  const isMoreToSee: boolean = servicesCount > maxNumberOfServicesDisplayed;

  return (
    <li className={cx(className, 'layout-column layout-align-start-stretch')}>
      <ServicePreview
        service={service}
        toggled={toggled}
        hasInfo={hasInfo}
        servicesCount={servicesCount}
        totalWorksCount={totalWorksCount}
        handlePanelToggle={() => hasInfo && setToggled(!toggled)}
      />
      <Divider className={cx(lastItem && !isMoreToSee && 'hide')} />
      <div
        className={cx(
          toggled ? 'show' : 'hide',
          lastItem && !isMoreToSee && 'border-top border-secondary-300',
          'sm:p-8 p-2 bg-secondary-50',
        )}>
        <ServiceAbout
          agencySlug={agencySlug}
          description={service.description ?? ''}
          serviceName={serviceName}
          skills={skills.filter((s): s is NonNullable<typeof s> => s != null)}
        />
        {worksCount > 0 && (
          <ServiceWorks
            works={works}
            worksCount={worksCount}
            serviceName={serviceName}
            handleActivePanel={handleActivePanel}
          />
        )}
        {reviewsCount > 0 && (
          <ServiceReviews
            handleActivePanel={handleActivePanel}
            reviews={reviews.filter((r): r is NonNullable<typeof r> => r != null)}
            serviceName={serviceName}
          />
        )}
        {clients.length > 0 && (
          <ServiceClients handleActivePanel={handleActivePanel} clients={clients} serviceName={serviceName} />
        )}
        <div className="pt-2 px-6 mt-4 layout-column">
          <Divider />
          <span className="small pt-8 max-sm:pb-6 flex-nogrow layout-row layout-align-start-center">
            <InfoOutlined style={{ fontSize: 14 }} className="mr-2 text-secondary-900" />
            <span className="text-secondary-900">
              <Trans i18nKey={'agency:profile.services.learnMore'} values={{ serviceName }}>
                <span>Learn more about </span>
                <a href={serviceUrl} className="underline cursor-pointer" target="_blank">
                  {'{{serviceName}}'}
                </a>
              </Trans>
            </span>
          </span>
        </div>
      </div>
      <Divider className={cx(toggled ? 'show' : 'hide')} />
    </li>
  );
};
